import React from "react";
import { connect } from "react-redux";
import { Col, Row } from "react-bootstrap";
import PropTypes from "prop-types";

import Table from "../../../layout/table";
import ConditionalButton from "../../../layout/conditional-button";

import { clientActivity, clearClientLines } from "../../../actions/trust.ledgers";
import { format as formatDate } from "../../../utils/date";
import { currency as formatCurrency } from "../../../utils/number";

const caption = (value) => String(value || "")
  .toLowerCase()
  .split("_")
  .filter(Boolean)
  .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
  .join(" ");

class TrustActivity extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      page: 0,
      size: 10,
      order: "entryDate",
      direction: "DESC",
      data: [],
      pages: 0,
      summary: {},
    };
  }

  componentDidMount = () => {
    this.loadData();
  };

  componentDidUpdate = (oldProps) => {
    if (oldProps.clientUuid !== this.props.clientUuid || oldProps.updated !== this.props.updated) {
      this.loadData();
    }
  };

  loadData = async () => {
    const result = await this.props.clientActivity({
      clientUuid: this.props.clientUuid,
      page: this.state.page,
      size: this.state.size,
      order: this.state.order,
      direction: this.state.direction,
    });

    const count = Number(result?.count || 0);
    this.setState({
      data: (result?.items || []).map((row) => ({
        ...row,
        entryDate: row.JournalEntry?.entryDate,
        referenceNumber: row.JournalEntry?.referenceNumber,
      })),
      pages: Math.ceil(count / this.state.size),
      summary: result?.summary || {},
    });
  };

  onPageChange = (page) => {
    this.setState({ page: Number(page) }, this.loadData);
  };

  onClearClick = async (row, cleared) => {
    await this.props.clearClientLines({
      clientUuid: this.props.clientUuid,
      lineUuids: [row.uuid],
      cleared,
    });
    await this.loadData();
  };

  onDateFormat = (value) => formatDate(value);

  onTypeFormat = (value) => caption(value);

  onAmountFormat = (value) => formatCurrency(Number(value || 0));

  onStatusFormat = (_value, row) => row.cleared
    ? <span className="badge bg-success">Cleared</span>
    : <span className="badge bg-light text-dark">Open</span>;

  onActionFormat = (_value, row) => (
    <ConditionalButton
      enabledVariant={row.cleared ? "outline-secondary" : "outline-primary"}
      display
      enabled
      onEnabledClick={() => this.onClearClick(row, !row.cleared)}
      icon={row.cleared ? "undo" : "check"}
    >
      {row.cleared ? "unclear" : "clear"}
    </ConditionalButton>
  );

  get headers() {
    return [
      {
        header: "Date",
        sortable: false,
        source: "entryDate",
        className: "text-muted text-small text-uppercase w-10",
        onRender: this.onDateFormat,
      },
      {
        header: "Type",
        sortable: false,
        source: "activityType",
        className: "text-muted text-small text-uppercase w-15",
        onRender: this.onTypeFormat,
      },
      {
        header: "Description",
        sortable: false,
        source: "memo",
        className: "text-muted text-small text-uppercase w-35",
      },
      {
        header: "Amount",
        sortable: false,
        source: "trustAmount",
        className: "text-muted text-small text-uppercase text-center w-10",
        dataClassName: "text-end",
        onRender: this.onAmountFormat,
      },
      {
        header: "Status",
        sortable: false,
        source: "cleared",
        className: "text-muted text-small text-uppercase text-center w-10",
        onRender: this.onStatusFormat,
      },
      {
        header: "",
        sortable: false,
        source: "uuid",
        className: "text-muted text-small text-uppercase w-10",
        onRender: this.onActionFormat,
      },
    ];
  }

  render() {
    const summary = this.state.summary || {};

    return (
      <Row className="g-3">
        <Col xs="12" className="d-flex align-items-center justify-content-between">
          <h2 className="small-title mb-0">Client Trust Ledger</h2>
          <div className="text-end small">
            <span className="me-3">Balance {formatCurrency(Number(summary.balance || 0))}</span>
            <span className="me-3">Uncleared {formatCurrency(Number(summary.uncleared || 0))}</span>
            {summary.hasShortage ? (
              <span className="text-danger">Shortage {formatCurrency(Number(summary.shortage || 0))}</span>
            ) : null}
          </div>
        </Col>
        <Col xs="12">
          <Table
            headers={this.headers}
            page={this.state.data}
            size={this.state.size}
            index={this.state.page}
            pages={this.state.pages}
            onPageChange={this.onPageChange}
            searchable={false}
            sortable={false}
            sizable={false}
          />
        </Col>
      </Row>
    );
  }
}

TrustActivity.propTypes = {
  clientUuid: PropTypes.string,
};

export default connect(null, {
  clientActivity,
  clearClientLines,
})(TrustActivity);
